import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Injector,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs/Subscription";
import { AppMenu } from "./app-menu";
import { AppMenuItem } from "./app-menu-item";

import { PermissionCheckerService } from "@abp/auth/permission-checker.service";
import { AppComponentBase } from "@shared/common/app-component-base";
import { AppSessionService } from "@shared/common/session/app-session.service";
import { AppUiCustomizationService } from "@shared/common/ui/app-ui-customization.service";
import { AppNavigationService } from "./app-navigation.service";
import { AppConsts } from "@shared/AppConsts";
import {
    IsTenantAvailableOutput,
    TenantListDto,
} from "@shared/service-proxies/service-proxies";

/** A visible menu entry with its place in the tree. */
interface SideNavNode {
    /** Position path, e.g. "4-1-2"; unique, used for ids and open state. */
    id: string;
    item: AppMenuItem;
    depth: number;
    parent: SideNavNode;
    children: SideNavNode[];
}

/**
 * Left navigation. Angular owns it: the tree is built once from AppNavigationService (same permission rules as before),
 * the active page is the one leaf whose route matches the URL, its ancestors are opened on every navigation, and groups
 * open as an accordion. Metronic's mMenu is not attached (no #m_ver_menu / m-menu markup); Metronic still provides the
 * fixed aside, the minimized width and the mobile off-canvas.
 */
@Component({
    templateUrl: "./side-bar-menu.component.html",
    styleUrls: ["./side-bar-menu.component.less"],
    selector: "side-bar-menu",
    encapsulation: ViewEncapsulation.None,
})
export class SideBarMenuComponent extends AppComponentBase implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("navElement") navElement: ElementRef;
    menu: AppMenu = null;
    currentTenantIsParent: boolean = false;
    nodes: SideNavNode[] = [];
    activeId: string = null;
    /** Ancestors of the active page. */
    activeTrail: { [id: string]: boolean } = {};
    /** Groups currently expanded. */
    openIds: { [id: string]: boolean } = {};
    /** Label shown next to an icon while the aside is minimized; null when hidden. */
    tooltip: { text: string; top: number; start: number } = null;

    private routerSubscription: Subscription;

    constructor(
        injector: Injector,
        public permission: PermissionCheckerService,
        private _appSessionService: AppSessionService,
        private _uiCustomizationService: AppUiCustomizationService,
        private _appNavigationService: AppNavigationService,
        private _router: Router
    ) {
        super(injector);
    }

    ngOnInit() {
        this.menu = this._appNavigationService.getMenu();
        // var tenantInfo = abp.utils.getCookieValue(
        //     AppConsts.appSessionItems.tenantInfo
        // );

        var tenantInfo = abp.utils.getCookieValue(
            AppConsts.appSessionItems.tenantInfo
        );
        if (tenantInfo) {
            var tenant = JSON.parse(tenantInfo) as TenantListDto;
            if (tenant) {
                var currentIsParentTenant = abp.utils.getCookieValue(
                    AppConsts.appSessionItems.isParentTenantManage
                );
                this.currentTenantIsParent =
                    tenant.isParent && parseInt(currentIsParentTenant) == 0;
            }
        }

        this.nodes = this.buildNodes(this.menu.items, null);
        this.syncWithUrl(this._router.url);
        this.routerSubscription = this._router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.syncWithUrl(event.urlAfterRedirects);
                setTimeout(() => this.revealActive(), 0);
                // A page was picked: close the mobile off-canvas (no-op on desktop).
                if (typeof mLayout !== "undefined" && mLayout.closeMobileAsideMenuOffcanvas) {
                    mLayout.closeMobileAsideMenuOffcanvas();
                }
            }
        });
    }

    ngAfterViewInit() {
        // After a refresh or a direct link the current page may sit below the fold of a long group.
        setTimeout(() => this.revealActive(), 0);
    }

    ngOnDestroy() {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    showMenuItem(menuItem): boolean {
        if (
            menuItem.permissionName ===
                "Pages.Administration.Tenant.SubscriptionManagement" &&
            this._appSessionService.tenant &&
            !this._appSessionService.tenant.edition
        ) {
            return false;
        }

        if (menuItem.permissionName&&(menuItem.permissionName as string).toLowerCase()!="Pages.Franshieses".toLowerCase()) {
            return this.permission.isGranted(menuItem.permissionName);
        }
        if (menuItem.forParent) {
            return this.currentTenantIsParent&&this.permission.isGranted(menuItem.permissionName);
        } else {
            if (menuItem.items && menuItem.items.length) {
                return this._appNavigationService.checkChildMenuItemPermission(
                    menuItem
                );
            }
        }

        return true;
    }

    isOpen(node: SideNavNode): boolean {
        return !!this.openIds[node.id];
    }

    /** Opens a group (closing its open siblings) or closes it. While minimized, the aside expands first. */
    toggle(node: SideNavNode): void {
        if (this.isMinimized()) {
            this.hideTooltip();
            this._uiCustomizationService.setAsideCollapsed(false);
            this.open(node);
            return;
        }
        if (this.isOpen(node)) {
            delete this.openIds[node.id];
        } else {
            this.open(node);
        }
    }

    showTooltip(event: Event, node: SideNavNode): void {
        if (!this.isMinimized() || node.depth > 0) {
            return;
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const rtl = document.documentElement.getAttribute("dir") === "rtl";
        this.tooltip = {
            text: this.l(node.item.name),
            top: rect.top + rect.height / 2,
            // Distance from the aside's edge: left in LTR, right in RTL.
            start: (rtl ? window.innerWidth - rect.left : rect.right) + 8,
        };
    }

    hideTooltip(): void {
        this.tooltip = null;
    }

    @HostListener("document:keydown.escape")
    onEscape(): void {
        if (typeof mLayout !== "undefined" && mLayout.closeMobileAsideMenuOffcanvas) {
            mLayout.closeMobileAsideMenuOffcanvas();
        }
    }

    trackById(index: number, node: SideNavNode): string {
        return node.id;
    }

    isRtl(): boolean {
        return document.documentElement.getAttribute("dir") === "rtl";
    }

    private isMinimized(): boolean {
        // Up to Metronic's lg breakpoint (992px) the aside is an off-canvas panel at full width, whatever the body class says.
        return this._uiCustomizationService.isAsideMinimized() && window.innerWidth > 992;
    }

    /**
     * Scrolls the menu (never the page) so that the current page's entry is in view. On a full page load the layout's
     * body classes (fixed aside) arrive a moment later, so it retries briefly until the menu can scroll.
     */
    private revealActive(attemptsLeft: number = 20): void {
        const nav = this.navElement && (this.navElement.nativeElement as HTMLElement);
        const active = nav && (nav.querySelector(".pm-sidenav__link--active") as HTMLElement);
        if (!active) {
            return;
        }
        const navRect = nav.getBoundingClientRect();
        const rect = active.getBoundingClientRect();
        if (rect.top >= navRect.top && rect.bottom <= navRect.bottom) {
            return;
        }
        if (nav.scrollHeight <= nav.clientHeight) {
            if (attemptsLeft > 0) {
                setTimeout(() => this.revealActive(attemptsLeft - 1), 100);
            }
            return;
        }
        nav.scrollTop += rect.top - navRect.top - (navRect.height - rect.height) / 2;
    }

    private open(node: SideNavNode): void {
        const siblings = node.parent ? node.parent.children : this.nodes;
        siblings.forEach((sibling) => delete this.openIds[sibling.id]);
        for (let current = node; current; current = current.parent) {
            this.openIds[current.id] = true;
        }
    }

    private buildNodes(items: AppMenuItem[], parent: SideNavNode): SideNavNode[] {
        const nodes: SideNavNode[] = [];
        items.forEach((item, index) => {
            if (!this.showMenuItem(item)) {
                return;
            }
            const node: SideNavNode = {
                id: parent ? `${parent.id}-${index}` : `${index}`,
                item: item,
                depth: parent ? parent.depth + 1 : 0,
                parent: parent,
                children: [],
            };
            node.children = item.items && item.items.length ? this.buildNodes(item.items, node) : [];
            // A group whose children are all hidden is not shown (as before, through checkChildMenuItemPermission).
            if (item.items && item.items.length && !node.children.length) {
                return;
            }
            nodes.push(node);
        });
        return nodes;
    }

    /**
     * Marks the leaf for the URL (exact route, else the longest route the URL continues with, e.g. a page's child route)
     * and opens its ancestors. Only one entry is ever active; the first one wins if two share a route.
     */
    private syncWithUrl(url: string): void {
        const path = (url || "").split(/[?#;]/)[0].replace(/\/+$/, "");
        let best: SideNavNode = null;
        let bestLength = -1;
        const visit = (nodes: SideNavNode[]) => {
            nodes.forEach((node) => {
                if (node.children.length) {
                    visit(node.children);
                    return;
                }
                const route = (node.item.route || "").replace(/\/+$/, "");
                if (!route) {
                    return;
                }
                const matches = path === route || path.indexOf(route + "/") === 0;
                if (matches && route.length > bestLength) {
                    best = node;
                    bestLength = route.length;
                }
            });
        };
        visit(this.nodes);

        this.activeId = best ? best.id : null;
        this.activeTrail = {};
        if (best) {
            for (let ancestor = best.parent; ancestor; ancestor = ancestor.parent) {
                this.activeTrail[ancestor.id] = true;
            }
            if (best.parent) {
                this.open(best.parent);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/layout/nav/side-bar-menu.component.ts